import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ROLE_CATEGORIES } from "@/pages/RegisterStaff";
import { Plus, Briefcase, Trash2, Users2, CheckCircle2, User, FileText } from "lucide-react";

const emptyForm = () => ({
  title: "",
  location: "",
  date: "",
  start_time: "",
  end_time: "",
  rate_of_pay: 20,
  required_role: ROLE_CATEGORIES[0],
  workers_needed: 1,
  hours: 8,
  country_scope: "IE",
  required_licence: "ANY",
  service_tier: "platform_match",
  other_requirements: "",
  dress_code: "",
  site_instructions: "",
  call_schedule: "",
  sops: "",
  agreement_terms: "",
  contact_instructions: "",
});

export default function CompanyJobs() {
  const [jobs, setJobs] = useState([]);
  const [minRates, setMinRates] = useState({ IE: {}, UK: {} });
  const [tiers, setTiers] = useState({ tiers: [], cpo_roles: [] });
  const [f, setF] = useState(emptyForm());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applicantsFor, setApplicantsFor] = useState(null); // {jobId, data}
  const location = useLocation();

  const load = async () => {
    try { const { data } = await api.get("/jobs?scope=mine"); setJobs(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    api.get("/public/minimum-rates").then((r) => setMinRates(r.data || { IE: {}, UK: {} })).catch(() => {});
    api.get("/public/service-tiers").then((r) => setTiers(r.data || { tiers: [], cpo_roles: [] })).catch(() => {});
  }, []);

  // Force elite_protection for CPO / Senior CPO roles (V3 spec §6)
  const isCpoRole = tiers.cpo_roles?.includes(f.required_role);
  useEffect(() => {
    if (isCpoRole && f.service_tier !== "elite_protection") {
      setF((prev) => ({ ...prev, service_tier: "elite_protection" }));
    } else if (!isCpoRole && f.service_tier === "elite_protection") {
      setF((prev) => ({ ...prev, service_tier: "platform_match" }));
    } else if (f.service_tier === "assisted_match") {
      // Migrate any old default to the corrected V3 key
      setF((prev) => ({ ...prev, service_tier: "tracked_deployment" }));
    }
  }, [isCpoRole]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Compute the minimum rate applicable to the current form selection (live hint)
  const applicableMinimum = React.useMemo(() => {
    if (!f.required_role) return null;
    const c = f.country_scope || "IE";
    if (c === "BOTH") return Math.max(minRates.IE?.[f.required_role] || 0, minRates.UK?.[f.required_role] || 0);
    return minRates?.[c]?.[f.required_role] || null;
  }, [f.required_role, f.country_scope, minRates]);
  const belowMin = applicableMinimum && Number(f.rate_of_pay) < applicableMinimum;

  // Support pre-fill via ?workers=5&role=Security%20Guard (used by CompanyTeams fallback)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const workers = params.get("workers");
    const role = params.get("role");
    const prefillTeam = params.get("team");
    if (workers || role || prefillTeam) {
      setF((prev) => ({
        ...prev,
        workers_needed: workers ? Number(workers) : prev.workers_needed,
        required_role: ROLE_CATEGORIES.includes(role) ? role : prev.required_role,
        title: prefillTeam ? `Team of ${workers || prev.workers_needed} ${role || prev.required_role} needed` : prev.title,
      }));
      setDialogOpen(true);
    }
  }, [location.search]);

  const post = async () => {
    try {
      const job = await api.post("/jobs", { ...f, rate_of_pay: Number(f.rate_of_pay), workers_needed: Number(f.workers_needed), hours: Number(f.hours) });
      // If 5% Managed Deployment tier — also create a Managed Deployment Request
      if (f.service_tier === "tracked_deployment") {
        try {
          await api.post("/company/managed-deployments", {
            job_title: f.title,
            location: f.location,
            date: f.date,
            start_time: f.start_time,
            end_time: f.end_time,
            workers_needed: Number(f.workers_needed),
            staff_type: f.required_role,
            hours: Number(f.hours),
            rate_of_pay: Number(f.rate_of_pay),
            notes: f.site_instructions,
            job_id: job?.data?.id,
          });
          toast.success("Job posted · Managed Deployment Request sent to SecuSource Operations");
        } catch (e) {
          toast.warning("Job posted, but the Managed Deployment Request failed: " + formatApiError(e.response?.data?.detail));
        }
      } else {
        toast.success("Job posted — professionals will start applying shortly");
      }
      setDialogOpen(false);
      setF(emptyForm());
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeJob = async (id) => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Job deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openApplicants = async (jobId) => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/applicants`);
      setApplicantsFor({ jobId, data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const acceptApplicant = async (jobId, staffId) => {
    try {
      await api.post(`/jobs/${jobId}/accept/${staffId}`);
      toast.success("Accepted — the professional has been notified");
      const { data } = await api.get(`/jobs/${jobId}/applicants`);
      setApplicantsFor({ jobId, data });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="company-jobs-page">
      <div className="page-hero flex flex-wrap gap-4 items-start justify-between">
        <div className="relative z-10">
          <div className="eyebrow eyebrow-brand mb-2">Jobs Board</div>
          <h1 className="text-3xl font-outfit font-bold txt-hi flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-brand" /> Post Jobs
          </h1>
          <p className="txt-md text-sm font-manrope mt-1">Broadcast to every verified pro.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-brand relative z-10" data-testid="company-post-job-btn">
              <Plus className="w-4 h-4 mr-1" /> Post a Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>Post a Job</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Event security at Aviva Stadium" data-testid="company-job-title" /></div>
              <div><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Dublin, Ireland" data-testid="company-job-location" /></div>
              <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
              <div>
                <Label>Rate €/hr {applicableMinimum ? <span className="text-[10px] text-white/60 font-normal">(min €{applicableMinimum}/hr)</span> : null}</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={f.rate_of_pay}
                  onChange={(e) => setF({ ...f, rate_of_pay: e.target.value })}
                  className={belowMin ? "border-red-400 focus-visible:ring-red-400" : ""}
                  data-testid="company-job-rate"
                />
                {belowMin && (
                  <p className="text-xs text-red-600 font-manrope mt-1">
                    Below the enforced minimum of €{applicableMinimum}/hr — job will be rejected. SecuSource enforces ERO/SIA minimum rates.
                  </p>
                )}
              </div>
              <div><Label>Start time</Label><Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
              <div><Label>End time</Label><Input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
              <div><Label>Workers needed</Label><Input type="number" min={1} value={f.workers_needed} onChange={(e) => setF({ ...f, workers_needed: e.target.value })} data-testid="company-job-workers" /></div>
              <div><Label>Hours</Label><Input type="number" step="0.5" min={1} value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} /></div>
              <div className="md:col-span-2">
                <Label>Required role</Label>
                <Select value={f.required_role} onValueChange={(v) => setF({ ...f, required_role: v })}>
                  <SelectTrigger data-testid="company-job-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLE_CATEGORIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={f.country_scope} onValueChange={(v) => setF({ ...f, country_scope: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IE">Ireland only</SelectItem>
                    <SelectItem value="UK">United Kingdom only</SelectItem>
                    <SelectItem value="BOTH">Ireland or United Kingdom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Required licence</Label>
                <Select value={f.required_licence} onValueChange={(v) => setF({ ...f, required_licence: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any (PSA or SIA)</SelectItem>
                    <SelectItem value="PSA">PSA (Ireland)</SelectItem>
                    <SelectItem value="SIA">SIA (UK)</SelectItem>
                    <SelectItem value="NONE">None required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2"><Label>Other requirements</Label><Textarea rows={2} value={f.other_requirements} onChange={(e) => setF({ ...f, other_requirements: e.target.value })} placeholder="e.g., Own vehicle, First Aid cert…" /></div>
              <div className="md:col-span-2"><Label>Dress code</Label><Input value={f.dress_code} onChange={(e) => setF({ ...f, dress_code: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Site instructions</Label><Textarea rows={2} value={f.site_instructions} onChange={(e) => setF({ ...f, site_instructions: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>SOPs</Label><Textarea rows={2} value={f.sops} onChange={(e) => setF({ ...f, sops: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Contact / reporting</Label><Textarea rows={2} value={f.contact_instructions} onChange={(e) => setF({ ...f, contact_instructions: e.target.value })} /></div>
            </div>

            {/* V3 Service Tier Picker — visual-first */}
            {tiers.tiers?.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="v3-tier-picker">
                <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold">Choose a tier</div>
                <div className="grid gap-2 md:grid-cols-3">
                  {tiers.tiers.map((t) => {
                    const disabled = isCpoRole ? t.key !== "elite_protection" : t.key === "elite_protection";
                    const selected = f.service_tier === t.key;
                    const short = {
                      "platform_match": "Hire direct",
                      "tracked_deployment": "+ Ops Room",
                      "elite_protection": "Fully managed",
                    }[t.key] || "";
                    return (
                      <button
                        type="button"
                        key={t.key}
                        disabled={disabled}
                        onClick={() => !disabled && setF({ ...f, service_tier: t.key })}
                        className={`tier-card ${selected ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                        data-testid={`v3-tier-${t.key}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="tier-num">{t.percent}<sup>%</sup></div>
                          {selected && <CheckCircle2 className="w-5 h-5 text-brand mt-2" />}
                        </div>
                        <div className="mt-3 font-outfit font-bold text-sm" style={{color: "#F8FAFC"}}>{t.label.replace(/^\d+% /, "")}</div>
                        <div className="text-xs font-manrope mt-1" style={{color: "#94A3B8"}}>{short}</div>
                        {disabled && <div className="text-[10px] uppercase tracking-widest font-outfit font-bold mt-3" style={{color: "#64748B"}}>{isCpoRole ? "Guard only" : "CPO only"}</div>}
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const t = tiers.tiers.find((x) => x.key === f.service_tier);
                  if (!t) return null;
                  const bv = Number(f.rate_of_pay || 0) * Number(f.workers_needed || 1) * Number(f.hours || 0);
                  const fee = bv * (t.percent / 100);
                  return (
                    <div className="mt-2 flex items-center justify-between rounded-md bg-[#0E0E0E] border border-white/10 px-3 py-2" data-testid="v3-fee-preview">
                      <span className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Fee preview</span>
                      <span className="font-outfit font-bold text-white">€{fee.toFixed(2)} <span className="text-white/60 text-xs font-normal">/ €{bv.toFixed(2)}</span></span>
                    </div>
                  );
                })()}
              </div>
            )}

            <DialogFooter>
              <Button onClick={post} className="bg-brand hover:bg-brand-hover text-white" data-testid="company-job-post-submit">Post Job</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {jobs.length === 0 && (
        <div className="card-dark p-10 text-center" data-testid="company-jobs-empty">
          <Briefcase className="w-10 h-10 txt-lo mx-auto mb-3" />
          <div className="font-outfit font-semibold txt-hi mb-1">No jobs posted</div>
          <div className="txt-lo font-manrope text-sm mb-4">Post one to reach every verified pro.</div>
          <Button onClick={() => setDialogOpen(true)} className="btn-brand">
            <Plus className="w-4 h-4 mr-1" /> Post your first job
          </Button>
        </div>
      )}

      <div className="grid gap-3">
        {jobs.map((j) => (
          <div key={j.id} className="card-dark card-dark-hover p-5 border-l-4 border-l-brand" data-testid={`company-job-${j.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {j.job_number && (
                    <span className="text-[10px] uppercase tracking-widest font-outfit font-bold text-brand bg-brand/15 border border-brand/40 rounded px-1.5 py-0.5" data-testid={`company-job-number-${j.id}`}>
                      {j.job_number}
                    </span>
                  )}
                  <span className="font-outfit font-semibold txt-hi">{j.title}</span>
                  {j.status === "pending_review" && <span className="spill spill-standby"><span className="dot" /> Pending review</span>}
                  {j.status === "resubmit_requested" && <span className="spill spill-emergency"><span className="dot" /> Resubmit</span>}
                  {j.status === "open" && <span className="spill spill-live"><span className="dot" /> Live</span>}
                  {!["pending_review", "resubmit_requested", "open"].includes(j.status) && <span className="spill spill-neutral"><span className="dot" /> {j.status}</span>}
                </div>
                <div className="eyebrow mt-2">{j.location} · {j.date} · €{j.rate_of_pay}/hr</div>
                <div className="text-xs txt-md font-manrope mt-1">
                  {j.required_role} · need {j.workers_needed} · {(j.country_scope || "IE") === "BOTH" ? "IE/UK" : (j.country_scope || "IE")} · {j.required_licence || "ANY"}
                </div>
                {j.resubmit_note && (
                  <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs font-manrope p-2" data-testid={`company-job-resubmit-note-${j.id}`}>
                    <span className="uppercase tracking-widest font-outfit font-bold mr-1">SecuSource:</span>{j.resubmit_note}
                  </div>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Link to={`/company/jobs/${j.id}`} data-testid={`company-open-job-${j.id}`}>
                  <Button variant="outline" size="sm" className="btn-ghost-dark">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Details
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={() => openApplicants(j.id)} className="btn-ghost-dark" data-testid={`company-view-applicants-${j.id}`}>
                  <Users2 className="w-3.5 h-3.5 mr-1" /> {(j.applicants || []).length}
                </Button>
                <Button variant="outline" size="sm" onClick={() => removeJob(j.id)} className="btn-ghost-dark hover:text-red-400" data-testid={`company-delete-job-${j.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!applicantsFor} onOpenChange={(v) => !v && setApplicantsFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Applicants</DialogTitle></DialogHeader>
          {!applicantsFor ? null : applicantsFor.data.applicants.length === 0 ? (
            <div className="text-white/60 font-manrope text-sm py-4">No applicants yet — check back soon.</div>
          ) : (
            <div className="space-y-2">
              {applicantsFor.data.applicants.map((p) => (
                <div key={p.id} className={`rounded-md border p-3 flex items-center justify-between ${applicantsFor.data.accepted_id === p.id ? "border-gold-300 bg-gold-50" : "border-white/10"}`} data-testid={`applicant-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-outfit font-semibold text-white flex items-center gap-1"><User className="w-4 h-4 text-brand" /> {p.full_name || "—"}</div>
                    <div className="text-xs text-white/60 font-manrope">{p.secusource_id} · {p.county || "—"} · {p.years_experience || 0} yr</div>
                  </div>
                  {applicantsFor.data.accepted_id === p.id ? (
                    <span className="inline-flex items-center gap-1 text-gold-600 text-sm font-manrope font-semibold">
                      <CheckCircle2 className="w-4 h-4" /> Accepted
                    </span>
                  ) : (
                    <Button onClick={() => acceptApplicant(applicantsFor.jobId, p.id)} className="bg-gold-500 hover:bg-gold-600 text-white" data-testid={`accept-applicant-${p.id}`}>
                      Accept
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
